import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { ChevronDown, Eye, EyeOff } from 'lucide-react';
import GOOGLE_ICON from '@/ui/assets/images/google-icon-logo.svg';
import LINKEDIN_ICON from '@/ui/assets/icons/linkedinicon.png';
import PROXI_BRAIN_LOGO from '@/ui/assets/images/proxivideobrainlogo.png';
import { useAuth } from '@/lib/auth-provider';
import { isFirstVisitToday } from '@/lib/utils';
import { hasStartedContributorOnboarding } from '@/lib/contributorProfile';
import { LANGUAGES } from '@/lib/constants';
import InterviewsListScreen from './InterviewsListScreen';
import ContributorOnboarding from './ContributorOnboarding';
import SpeechBubbleAnimation from '@/ui/components/SpeechBubbleAnimation';
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import {
  SIGN_IN_WEB,
  SEND_SET_PASSWORD_EMAIL,
} from '@/api/graphql/mutations';
import { CURRENT_USER } from '@/api/graphql/queries';
import { LightShell, LightMark, LightSpinner, FitLine } from '../light-ui';
import { PhoneAuthFlow, type PhoneAuthSuccessPayload } from '@/components/PhoneAuthFlow';

interface LandingScreenProps {
  onSignInSuccess?: () => void;
}

const LandingScreen: React.FC<LandingScreenProps> = ({ onSignInSuccess }) => {
  const { signInWithGoogle, signInWithLinkedIn, signInWithEmailPassword, signInWithPhone, loading, currentUser, authUser } = useAuth();
  // Phone-OTP is the default signup path; "Use email instead" switches.
  // Name + channel + consent are collected later in ContributorOnboarding.
  const [signupMethod, setSignupMethod] = useState<'phone' | 'email'>('phone');
  const [mode, setMode] = useState<'signin' | 'signup'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verifiedPhone, setVerifiedPhone] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingText, setLoadingText] = useState('Signing in…');
  const [selectedLanguage, setSelectedLanguage] = useState('ENG');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!(authUser && !authUser.isAnonymous));
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [justSignedUp, setJustSignedUp] = useState(false);
  const [showBubbleAnimation, setShowBubbleAnimation] = useState<boolean>(() => isFirstVisitToday() || true);

  const [signInWebMutation] = useMutation(SIGN_IN_WEB);
  const [sendSetPasswordEmail] = useMutation(SEND_SET_PASSWORD_EMAIL);

  const [magicLinkMode, setMagicLinkMode] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);

  const handleSendSetPasswordEmail = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter your email and we’ll send you a link.');
      return;
    }
    setResetSending(true);
    try {
      await sendSetPasswordEmail({ variables: { email: trimmed } });
      setResetSentTo(trimmed);
    } catch (err: any) {
      setError(err?.message || 'Could not send the email. Please try again.');
    } finally {
      setResetSending(false);
    }
  };

  useEffect(() => {
    if (authUser && !authUser.isAnonymous) setIsAuthenticated(true);
  }, [currentUser, authUser]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (loading || isSubmitting) {
      setLoadingText(mode === 'signup' ? 'Creating account…' : 'Signing in…');
      timeout = setTimeout(() => setLoadingText('Preparing your session…'), 2000);
    } else {
      setLoadingText(mode === 'signup' ? 'Creating account…' : 'Signing in…');
    }
    return () => clearTimeout(timeout);
  }, [loading, isSubmitting, mode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };
    if (showLanguageDropdown) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageDropdown]);

  const handleSignIn = async () => {
    setError(null);
    try {
      const user = await signInWithGoogle();
      if (user) {
        setIsAuthenticated(true);
        onSignInSuccess?.();
      } else {
        setTimeout(() => { setIsAuthenticated(true); onSignInSuccess?.(); }, 500);
      }
    } catch (err: any) {
      setError(err?.message || 'Sign in failed. Please try again.');
    }
  };

  const handleLinkedInSignIn = async () => {
    setError(null);
    try {
      const user = await signInWithLinkedIn();
      if (user) {
        setIsAuthenticated(true);
        onSignInSuccess?.();
      } else {
        setTimeout(() => { setIsAuthenticated(true); onSignInSuccess?.(); }, 500);
      }
    } catch (err: any) {
      setError(err?.message || 'LinkedIn sign in failed. Please try again.');
    }
  };

  const handleEmailPasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      if (!email || !password) {
        setError('Please enter both email and password');
        setIsSubmitting(false);
        return;
      }
      const user = await signInWithEmailPassword(email, password);
      if (user) {
        setIsAuthenticated(true);
        onSignInSuccess?.();
      } else {
        setTimeout(() => { setIsAuthenticated(true); onSignInSuccess?.(); }, 500);
      }
    } catch (err: any) {
      if (err?.code === 'auth/wrong-password' || err?.code === 'auth/user-not-found' || err?.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please try again.');
      } else if (err?.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (err?.code === 'auth/too-many-requests') {
        setError('Too many sign-in attempts. Please try again later.');
      } else {
        setError('Sign in failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneAuthSuccess = async ({ firebaseToken, phoneE164 }: PhoneAuthSuccessPayload) => {
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await signInWithPhone(firebaseToken, phoneE164);
      if (result.needsLinking) {
        setError(
          `An account already exists for ${result.maskedContact || 'this contact'}. Please sign up with email instead.`,
        );
        setSignupMethod('email');
        return;
      }
      if (result.error) {
        setError(result.error);
        return;
      }
      // Phone OTP succeeded — ContributorOnboarding handles name + consent.
      // Phone numbers are intrinsically verified by Firebase OTP, so no
      // email-verification gate is needed here.
      setVerifiedPhone(phoneE164);
      setJustSignedUp(true);
      setIsAuthenticated(true);
      onSignInSuccess?.();
    } catch (err: any) {
      setError(err?.message || 'Phone sign-in failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailPasswordSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      if (!email || !password) { setError('Please enter email and password'); setIsSubmitting(false); return; }
      if (password.length < 6) { setError('Password must be at least 6 characters'); setIsSubmitting(false); return; }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const authToken = await userCredential.user.getIdToken();
      // Send verification email — the needsEmailVerification gate below
      // blocks progression to onboarding until the user clicks the link.
      sendEmailVerification(userCredential.user).catch(() => {});

      // Name + channel/phone/consent are collected later in
      // ContributorOnboarding via savePersonalData.
      await signInWebMutation({
        variables: {
          token: authToken,
          extraData: {
            email,
            userType: 'MEMBER',
            language: selectedLanguage,
          },
        },
        refetchQueries: [{ query: CURRENT_USER }],
        awaitRefetchQueries: true,
      });

      // Don't set justSignedUp — that bypasses the verification gate.
      setIsAuthenticated(true);
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please sign in instead.');
        setMode('signin');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters.');
      } else {
        setError(err?.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialSignUp = async (providerKind: 'google' | 'linkedin') => {
    setError(null);
    setIsSubmitting(true);
    try {
      const provider = providerKind === 'google' ? new GoogleAuthProvider() : new OAuthProvider('oidc.linkedin');
      const result = await signInWithPopup(auth, provider);
      const authToken = await result.user.getIdToken();

      // OAuth supplies displayName + verified email — split for backend.
      const displayName = result.user.displayName || '';
      const parts = displayName.trim().split(/\s+/);
      const fName = parts[0] || '';
      const lName = parts.length > 1 ? parts.slice(1).join(' ') : '';

      await signInWebMutation({
        variables: {
          token: authToken,
          extraData: {
            email: result.user.email,
            firstName: fName,
            lastName: lName,
            displayName: displayName || undefined,
            userType: 'MEMBER',
            language: selectedLanguage,
          },
        },
        refetchQueries: [{ query: CURRENT_USER }],
        awaitRefetchQueries: true,
      });

      setJustSignedUp(true);
      setIsAuthenticated(true);
      onSignInSuccess?.();
    } catch (err: any) {
      setError(err?.message || `Failed to sign up with ${providerKind === 'google' ? 'Google' : 'LinkedIn'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Brand-new signups (justSignedUp) always get onboarding; anyone with ANY
  // prior onboarding progress — a channel/consent or saved profile basics —
  // is never walked through it again.
  const needsOnboarding =
    isAuthenticated &&
    !onboardingDismissed &&
    (justSignedUp ||
      (currentUser?.personalData?.email &&
        !hasStartedContributorOnboarding(currentUser)));

  if (needsOnboarding) {
    return (
      <ContributorOnboarding
        open={true}
        onComplete={() => setOnboardingDismissed(true)}
        userName={currentUser?.personalData?.firstName || undefined}
        verifiedPhone={verifiedPhone}
        currentUser={currentUser}
      />
    );
  }

  if (isAuthenticated && !onboardingDismissed && !currentUser && loading) {
    return (
      <LightShell>
        <main style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <LightSpinner size={48} />
            <span className="lp-section-sub">Loading…</span>
          </div>
        </main>
      </LightShell>
    );
  }

  const needsEmailVerification =
    isAuthenticated && !justSignedUp && authUser && !authUser.isAnonymous && !authUser.emailVerified;

  if (needsEmailVerification) {
    return (
      <LightShell>
        <main style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', padding: 24 }}>
          <div className="lp-card" style={{ maxWidth: 460, padding: 'clamp(28px, 5vw, 36px)', textAlign: 'center' }}>
            <LightMark src={PROXI_BRAIN_LOGO} size={56} />
            <h2 className="lp-section-heading" style={{ marginTop: 14, fontSize: 26 }}>Verify your email.</h2>
            <p className="lp-prose" style={{ fontSize: 14, marginTop: 8 }}>
              We sent a verification link to{' '}
              <span style={{ color: 'var(--lp-ink)' }}>{authUser.email}</span>. Please check your inbox and verify your email to continue.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
              <button
                type="button"
                className="lp-btn lp-btn-primary"
                onClick={async () => {
                  try {
                    await sendEmailVerification(authUser);
                    setError(null);
                    alert('Verification email sent! Please check your inbox.');
                  } catch {
                    setError('Could not send verification email. Please try again later.');
                  }
                }}
              >
                Resend verification email
              </button>
              <button
                type="button"
                className="lp-btn"
                onClick={async () => {
                  await authUser.reload();
                  if (authUser.emailVerified) {
                    await authUser.getIdToken(true);
                    window.location.reload();
                  } else {
                    setError('Email not yet verified. Please check your inbox.');
                  }
                }}
              >
                I've verified my email
              </button>
              <button
                type="button"
                className="lp-btn-link"
                onClick={async () => {
                  await auth.signOut();
                  setIsAuthenticated(false);
                }}
              >
                Sign out
              </button>
            </div>
            {error && <div className="lp-error" style={{ marginTop: 14, textAlign: 'left' }}>{error}</div>}
          </div>
        </main>
      </LightShell>
    );
  }

  if (isAuthenticated) {
    return <InterviewsListScreen onSignOut={() => setIsAuthenticated(false)} />;
  }

  const disabled = isSubmitting || loading;

  return (
    <LightShell scroll>
      {showBubbleAnimation && <SpeechBubbleAnimation onComplete={() => setShowBubbleAnimation(false)} />}

      {mode === 'signup' && (
        <div
          ref={languageDropdownRef}
          style={{
            position: 'absolute',
            top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
            right: 16,
            zIndex: 10,
          }}
        >
          <button
            type="button"
            onClick={() => setShowLanguageDropdown(v => !v)}
            disabled={disabled}
            className="lp-frost-chip"
            style={{ cursor: disabled ? 'not-allowed' : 'pointer', padding: '6px 10px', opacity: disabled ? 0.6 : 1 }}
            aria-haspopup="listbox"
            aria-expanded={showLanguageDropdown}
          >
            <span>{selectedLanguage}</span>
            <ChevronDown style={{ width: 13, height: 13, transition: 'transform 180ms ease', transform: showLanguageDropdown ? 'rotate(180deg)' : 'rotate(0)' }} />
          </button>
          {showLanguageDropdown && (
            <div
              role="listbox"
              className="lp-card lp-card-tight"
              style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: 180, maxHeight: 240, overflowY: 'auto', padding: 4 }}
            >
              {LANGUAGES.map(lang => {
                const selected = selectedLanguage === lang.code;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => { setSelectedLanguage(lang.code); setShowLanguageDropdown(false); }}
                    style={{
                      width: '100%', padding: '8px 10px', border: 0,
                      borderRadius: '2px 10px 2px 10px',
                      background: selected ? 'rgba(236,72,153,0.10)' : 'transparent',
                      color: selected ? 'var(--lp-ink)' : 'var(--lp-ink-soft)',
                      fontFamily: 'var(--lp-mono)', fontSize: 12, letterSpacing: '0.06em',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <span>{lang.label}</span>
                    <span style={{ fontSize: 10, color: 'var(--lp-ink-mute)' }}>{lang.code}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <main
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'calc(env(safe-area-inset-top, 0px) + 24px) clamp(16px, 4vw, 32px) calc(env(safe-area-inset-bottom, 0px) + 80px)',
        }}
      >
        <div className="lp-card" style={{ width: '100%', maxWidth: 500, padding: 'clamp(24px, 4vw, 36px)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14, marginBottom: 18 }}>
            <LightMark src={PROXI_BRAIN_LOGO} size={56} imgScale={0.72} />
            <h2 className="lp-section-heading" style={{ fontSize: 'clamp(22px, 3.4vw, 30px)' }}>
              Hi, I'm Proxi Interviewer.
            </h2>
            <p className="lp-prose" style={{ fontSize: 14, maxWidth: 380 }}>
              Access your interviews or leave a story tip.
            </p>
          </div>

          {error && <div className="lp-error" style={{ marginBottom: 14 }}>{error}</div>}

          {mode === 'signup' && signupMethod === 'phone' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <PhoneAuthFlow onSuccess={handlePhoneAuthSuccess} />
              <button
                type="button"
                onClick={() => { setSignupMethod('email'); setError(null); }}
                className="lp-btn-link"
                style={{ alignSelf: 'center', fontSize: 12 }}
              >
                Use email instead
              </button>
              <p className="lp-prose" style={{ fontSize: 13, textAlign: 'center', marginTop: 8 }}>
                Already have an account?{' '}
                <button type="button" onClick={() => { setMode('signin'); setError(null); }} className="lp-btn-link">Sign in</button>
              </p>
            </div>
          )}

          {(mode === 'signin' || signupMethod === 'email') && (
          <>
          <form
            onSubmit={(e) => {
              if (magicLinkMode) return handleSendSetPasswordEmail(e);
              return mode === 'signin' ? handleEmailPasswordSignIn(e) : handleEmailPasswordSignUp(e);
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              disabled={disabled}
              className="lp-input"
            />

            {!magicLinkMode && (
              <div className="lp-input-affix">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  disabled={disabled}
                  className="lp-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="lp-input-affix-end"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff style={{ width: 16, height: 16 }} /> : <Eye style={{ width: 16, height: 16 }} />}
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={disabled || resetSending}
              className="lp-btn lp-btn-primary"
              style={{ marginTop: 4 }}
            >
              {(isSubmitting || resetSending) ? (
                <>
                  <span className="lp-spinner-sm" />
                  <span>{resetSending ? 'Sending link…' : loadingText}</span>
                </>
              ) : (
                <span>{magicLinkMode ? 'Send link' : mode === 'signin' ? 'Sign in with email' : 'Sign up'}</span>
              )}
            </button>

            {mode === 'signin' && (
              <div style={{ textAlign: 'center', marginTop: 6 }}>
                {resetSentTo ? (
                  <p className="lp-prose" style={{ fontSize: 13 }}>
                    Check <span style={{ color: 'var(--lp-ink)' }}>{resetSentTo}</span> for a link to set your password.
                  </p>
                ) : magicLinkMode ? (
                  <button
                    type="button"
                    onClick={() => { setMagicLinkMode(false); setError(null); }}
                    disabled={resetSending}
                    className="lp-btn-link"
                  >
                    ← Back to sign in
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setMagicLinkMode(true); setError(null); }}
                    disabled={disabled}
                    className="lp-btn-link"
                  >
                    Forgot password, or don't have one yet?
                  </button>
                )}
              </div>
            )}
          </form>

          {!magicLinkMode && (
            <>
              {mode === 'signup' && (
                <div style={{ textAlign: 'center', marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => { setSignupMethod('phone'); setError(null); }}
                    className="lp-btn-link"
                    style={{ fontSize: 12 }}
                  >
                    Use phone number instead
                  </button>
                </div>
              )}

              <div className="lp-divider"><span>or</span></div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  type="button"
                  onClick={mode === 'signin' ? handleSignIn : () => handleSocialSignUp('google')}
                  disabled={disabled}
                  className="lp-btn"
                >
                  <img src={GOOGLE_ICON} width={20} height={20} alt="" />
                  <span>{loading ? loadingText : 'Continue with Google'}</span>
                </button>
                <button
                  type="button"
                  onClick={mode === 'signin' ? handleLinkedInSignIn : () => handleSocialSignUp('linkedin')}
                  disabled={disabled}
                  className="lp-btn"
                >
                  <img src={LINKEDIN_ICON} width={20} height={20} alt="" />
                  <span>{loading ? loadingText : 'Continue with LinkedIn'}</span>
                </button>
              </div>

              <p className="lp-prose" style={{ fontSize: 13, textAlign: 'center', marginTop: 16 }}>
                {mode === 'signin' ? (
                  <>
                    Don't have an account?{' '}
                    <button type="button" onClick={() => { setMode('signup'); setSignupMethod('phone'); setError(null); }} className="lp-btn-link">Sign up</button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button type="button" onClick={() => { setMode('signin'); setError(null); }} className="lp-btn-link">Sign in</button>
                  </>
                )}
              </p>
            </>
          )}
          </>
          )}

          {/* FitLine keeps the whole sentence on one line at any viewport,
              shrinking it as needed instead of wrapping. */}
          <FitLine style={{
            marginTop: 18,
            fontFamily: 'var(--lp-mono)',
            fontSize: 10,
            letterSpacing: '0.04em',
            color: 'var(--lp-ink-mute)',
            lineHeight: 1.7,
          }}>
            By continuing, you accept our{' '}
            <a href="https://proxiaistudio.com/privacy" target="_blank" rel="noopener noreferrer" className="lp-link">Privacy Policy</a>{' '}
            and{' '}
            <a href="https://proxiaistudio.com/terms" target="_blank" rel="noopener noreferrer" className="lp-link">Terms of Use</a>.
          </FitLine>
        </div>
      </main>

      <footer
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
        role="contentinfo"
      >
        <span className="lp-footer-link">
          Powered by{' '}
          <a href="https://www.ProxiAIStudio.com" target="_blank" rel="noopener noreferrer">ProxiAIStudio.com</a>
        </span>
      </footer>
    </LightShell>
  );
};

export default LandingScreen;
